<template>
  <Teleport to="body">
    <div class="fixed bottom-12 right-6 z-[60]">
      <div class="relative">
        <button
          class="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border bg-white text-white shadow-xl hover:brightness-95"
          :aria-label="$t('ai_assistant.widget_aria_open')"
          @click="toggleWidget"
        />

        <div
          class="gradient-pulse absolute inset-0 rounded-full bg-gradient-to-b from-[#04CBDE] to-[#E329CE] opacity-40"
        ></div>
        <PRingLoader v-if="isAiChatLoading" class="absolute inset-0 z-20 flex items-center justify-center" :size="24" />
        <div v-else class="pointer-events-none absolute inset-0 z-20 -m-2">
          <img src="@/ai-assistant/assets/paygent-logo.svg" class="sparkle sparkle-center" aria-hidden="true" />
          <img src="@/ai-assistant/assets/paygent-logo.svg" class="sparkle sparkle-s1" aria-hidden="true" />
          <img src="@/ai-assistant/assets/paygent-logo.svg" class="sparkle sparkle-s2" aria-hidden="true" />
        </div>
      </div>

      <div
        v-if="isOpen"
        class="fixed bottom-24 right-6 z-[70] h-[560px] w-[380px] overflow-hidden rounded-xl border bg-white shadow-2xl"
      >
        <div class="flex h-full flex-col">
          <div class="flex items-center justify-between border-b border-p-gray-20 px-3 py-2">
            <div class="flex items-center gap-2">
              <img
                src="@/ai-assistant/assets/paygent-logo.svg"
                :alt="$t('ai_assistant.widget_logo_alt')"
                class="h-5 w-5"
              />
              <span class="text-sm font-semibold">Paygent</span>
              <BetaLabel />

              <PBtn
                v-if="threadId"
                size="sm"
                icon="flowbite:expand-outline"
                type="secondary-ghost"
                :to="{ name: 'ai-assistant', params: { thread_id: threadId } }"
                :disabled="isAnswerInProgress"
                :aria-label="$t('ai_assistant.widget_aria_expand')"
                @click="closeWidget"
              />
              <PBtn
                v-if="threadId"
                size="sm"
                icon="plus"
                type="secondary-ghost"
                :aria-label="$t('ai_assistant.widget_aria_new_thread')"
                @click="startNewThread"
              />
            </div>
            <PBtn icon="cancel" type="secondary-ghost" size="sm" @click="closeWidget" />
          </div>
          <PRingLoader v-if="isAiChatLoading" class="absolute inset-0 flex items-center justify-center" :size="64" />
          <AiChat
            v-if="isOpen"
            v-model:chat-history="messages"
            ws-endpoint="ai-assistant"
            :input-placeholder="$t('ai_assistant.widget_input_placeholder')"
            :thread-id="threadId"
            :welcome-message="welcomeMessage"
            :question-examples="availableQuestionExamples"
            :context-to-include="contextToInclude"
            widget-version
            @connected:thread-id="handleConnectedThreadId"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { PBtn, PRingLoader } from '@pequity/squirrel';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  doesPathMatchPattern,
  getContextToIncludeByRoutePath,
} from '@/ai-assistant/components/ai-assistant-widget/ai-assistant-widget.config';
import type { AiChatHistoryMessage } from '@/ai-assistant/components/ai-chat/ai-chat.types';
import BetaLabel from '@/ai-assistant/components/beta-label.vue';
import { useQuestionExamplesQuery } from '@/ai-assistant/query/conversation';

defineOptions({ name: 'AiAssistantWidget' });

const { t } = useI18n();

// Async Components
const isAiChatLoading = ref(false);

const AiChat = defineAsyncComponent({
  loader: () => {
    isAiChatLoading.value = true;

    return import('@/ai-assistant/components/ai-chat/ai-chat.vue')
      .then((component) => component)
      .catch((error) => {
        console.error('Failed to load AiChat component:', error);
        throw error;
      })
      .finally(() => {
        isAiChatLoading.value = false;
      });
  },
});

// Data
const route = useRoute();
const { data: questionExamples } = useQuestionExamplesQuery();
const messages = ref<AiChatHistoryMessage[]>([]);
const threadId = ref<string | null>(null);
const isOpen = ref(false);
const welcomeMessage = computed(() => (messages.value.length ? '' : t('ai_assistant.widget_welcome_message')));

// Computed
const availableQuestionExamples = computed(() => {
  if (!questionExamples.value) return [];

  const filtered = questionExamples.value.filter((example) =>
    example.paths.some((pattern) => doesPathMatchPattern(route.path, pattern))
  );

  if (filtered.length <= 3) return filtered;

  const shuffled = filtered.toSorted(() => Math.random() - 0.5);

  return shuffled.slice(0, 3);
});

const contextToInclude = computed(() => {
  return getContextToIncludeByRoutePath(route.path);
});

const isAnswerInProgress = computed(() => {
  return messages.value.length && messages.value[messages.value.length - 1].is_user;
});

// Methods
const handleConnectedThreadId = (id: string) => {
  if (!threadId.value && id) {
    threadId.value = id;
  }
};

const startNewThread = () => {
  messages.value = [];
  threadId.value = null;
};

const toggleWidget = () => {
  isOpen.value = !isOpen.value;
};

const closeWidget = () => {
  isOpen.value = false;
};
</script>

<style scoped>
.sparkle {
  position: absolute;
  transform-origin: center;
  opacity: 1;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.08));
  will-change: transform, opacity;
}

@keyframes gradientPulse {
  0% {
    transform: scale(1);
    opacity: 0.4;
  }
  8% {
    transform: scale(1.818);
    opacity: 0;
  }
  19% {
    transform: scale(1);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0.4;
  }
}

.gradient-pulse {
  animation: gradientPulse 4s ease-out 1.6s infinite;
}

@keyframes sparkleCenterPulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  8% {
    transform: translate(-50%, -50%) scale(1.4);
  }
  19% {
    transform: translate(-50%, -50%) scale(0.7);
  }
  30% {
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

.sparkle-center {
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  transform: translate(-50%, -50%);
  animation: sparkleCenterPulse 4s ease-out 1.6s infinite;
}

@keyframes sparkleSidePop1 {
  0% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 1;
  }
  8% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 1;
  }
  19% {
    transform: translate(var(--tx), var(--ty)) scale(0.35);
    opacity: 1;
  }
  30% {
    transform: translate(var(--tx), var(--ty)) scale(0.35);
    opacity: 1;
  }
  50% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 0;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 0;
  }
}

@keyframes sparkleSidePop2 {
  0% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 1;
  }
  8% {
    transform: translate(var(--tx), var(--ty)) scale(0.7);
    opacity: 1;
  }
  19% {
    transform: translate(var(--tx), var(--ty)) scale(0.25);
    opacity: 1;
  }
  50% {
    transform: translate(var(--tx), var(--ty)) scale(0.6);
    opacity: 1;
  }
  60% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 0;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 0;
  }
}

.sparkle-s1 {
  opacity: 0;
  width: 20px;
  height: 20px;
  animation: sparkleSidePop1 4s ease-out 1.6s infinite;
  --tx: -4px;
  --ty: -24px;
  top: 50%;
  left: 50%;
}
.sparkle-s2 {
  opacity: 0;
  width: 20px;
  height: 20px;
  animation: sparkleSidePop2 4s ease-out 1.6s infinite;
  --tx: 5px;
  --ty: -16px;
  top: 50%;
  left: 50%;
}
</style>
